import type { Logger } from "@expert/logging";

import { customCleanEnv, url } from "envalid";
import ky from "ky";

import { featureCache } from "./featureCache";
import { hashInfo } from "./hashInfo";

/**
 * The one warn shape this module logs. Declared as a single signature rather than reusing
 * `Pick<Logger, "warn">`, because that method is overloaded and TypeScript requires an implementation
 * to satisfy every overload, which no precise signature can do. A full {@link Logger} is assignable
 * to this, so {@link setFeatureLogger} still accepts one.
 */
interface FeatureLogger {
    warn(payload: { error: unknown }, message: string): void;
}

// Delegate at call time so spies on console.warn (e.g. in tests) still observe calls.
let logger: FeatureLogger = {
    warn: (payload, message) => {
        console.warn(payload, message);
    },
};

const env = customCleanEnv(
    import.meta.env,
    {
        VITE_API_K8S_FEATURE_FLAG_URL: url({
            default: "https://feature.remoteexpert.npr.aws.asurion.net/api",
        }),
    },
    (
        inputEnv,
    ): {
        apiK8sFeatureFlagUrl: string;
    } => ({
        apiK8sFeatureFlagUrl: inputEnv.VITE_API_K8S_FEATURE_FLAG_URL,
    }),
);

export function setFeatureLogger(newLogger: Logger) {
    logger = newLogger;
}

/*
 * key - feature key to fetch
 * context - context about the requester to determine the value of the key
 * cacheTtl - ttl of in-memory cache to reduce load on the feature toggle server (in seconds)
 */
export const getFeature = async <T>(
    key: string,
    context: Record<string, unknown>,
    cacheTtl = 60,
): Promise<T | undefined> => {
    try {
        const hashedInfo = await hashInfo(key + JSON.stringify(context));

        if (cacheTtl > 0 && featureCache.has(hashedInfo)) {
            return featureCache.get<T>(hashedInfo);
        }

        const { value } = await ky
            .post(`${env.apiK8sFeatureFlagUrl}/v1/feature/${key}/fetch`, {
                json: { context: { ...context } },
                retry: { limit: 3, methods: ["POST"], retryOnTimeout: true, jitter: true, delay: () => 0 },
            })
            .json<{ value: T }>();

        featureCache.set(hashedInfo, value, cacheTtl);
        return value;
    } catch (error) {
        logger.warn({ error }, "error fetching the feature key");
        return undefined;
    }
};
